"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";

import "./EducationSector.css";
import { content } from "./EducationSectorData";
import CommonButton from "@/app/components/ui/common/CommonButton/CommonButton";

const EducationSector = () => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const [slideDirection, setSlideDirection] = useState("forward");
    const [isComponentFullyVisible, setIsComponentFullyVisible] =
        useState(false);
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const triggerRefs = useRef([]);
    const isManualScrollRef = useRef(false);
    const isScrollingRef = useRef(false);
    const lastScrollTimeRef = useRef(0);
    const scrollDirectionRef = useRef("forward");
    const scrollQueueRef = useRef([]);
    const isProcessingQueueRef = useRef(false);
    const scrollAccumulatorRef = useRef(0);

    // Immediate response settings
    const DEBOUNCE_TIME = 200; // Debounce time for scroll events
    const SCROLL_THRESHOLD = 50; // Lowered for higher sensitivity
    const entryBufferRef = useRef(false);

    // Track scroll direction
    useEffect(() => {
        let lastY = window.scrollY;

        const handleGlobalScroll = () => {
            const currentY = window.scrollY;
            scrollDirectionRef.current =
                currentY > lastY ? "forward" : "backward";
            lastY = currentY;
        };

        window.addEventListener("scroll", handleGlobalScroll);

        return () => {
            window.removeEventListener("scroll", handleGlobalScroll);
        };
    }, []);

    // Set up component visibility observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    const direction = scrollDirectionRef.current;
                    setSlideDirection(direction);

                    if (!isInitialized) {
                        setIsInitialized(true);
                        const initialIndex =
                            direction === "forward"
                                ? 0
                                : content.data.length - 1;
                        setActiveSlide(initialIndex);
                        // Shortened buffer to minimize delay
                        entryBufferRef.current = true;
                        setTimeout(() => {
                            entryBufferRef.current = false;
                        }, 50); // Reduced from 200ms to 50ms
                    }
                }
            },
            { threshold: 0.1 } // Lowered threshold for earlier detection
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, [isInitialized]);

    // Process scroll queue
    const processScrollQueue = () => {
        if (isProcessingQueueRef.current || scrollQueueRef.current.length === 0)
            return;

        isProcessingQueueRef.current = true;
        const nextSlideIndex = scrollQueueRef.current.shift();

        isScrollingRef.current = true;
        setActiveSlide(nextSlideIndex);

        triggerRefs.current[nextSlideIndex]?.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });

        setTimeout(() => {
            isScrollingRef.current = false;
            isProcessingQueueRef.current = false;

            if (scrollQueueRef.current.length > 0) {
                processScrollQueue();
            }
        }, 200);
    };

    useEffect(() => {
        triggerRefs.current = Array(content.data.length)
            .fill()
            .map((_, i) => triggerRefs.current[i] || React.createRef());

        const observerOptions = {
            root: null,
            rootMargin: "-5% 0px",
            threshold: 0.02,
        };

        const handleIntersect = (entries) => {
            if (!isInitialized) return;

            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const index = triggerRefs.current.findIndex(
                        (ref) => ref.current === entry.target
                    );
                    if (
                        index !== -1 &&
                        !isManualScrollRef.current &&
                        !isScrollingRef.current
                    ) {
                        const now = Date.now();
                        if (now - lastScrollTimeRef.current > 100) {
                            lastScrollTimeRef.current = now;

                            const currentSlide = activeSlide;
                            const targetSlide = index;

                            if (currentSlide !== targetSlide) {
                                scrollQueueRef.current = [];
                                const nextSlide =
                                    currentSlide < targetSlide
                                        ? currentSlide + 1
                                        : currentSlide - 1;

                                scrollQueueRef.current.push(nextSlide);
                                processScrollQueue();
                            }
                        }
                    }
                }
            });
        };

        const observer = new IntersectionObserver(
            handleIntersect,
            observerOptions
        );

        if (isInitialized) {
            triggerRefs.current.forEach((ref) => {
                if (ref.current) {
                    observer.observe(ref.current);
                }
            });
        }

        const handleScroll = () => {
            if (containerRef.current) {
                const containerRect =
                    containerRef.current.getBoundingClientRect();
                const isContainerVisible =
                    containerRect.top < window.innerHeight &&
                    containerRect.bottom > 0;

                const dotsElement = containerRef.current.querySelector(
                    ".progress-indicator"
                );
                if (dotsElement) {
                    dotsElement.style.display = isContainerVisible
                        ? "flex"
                        : "none";
                }
            }
        };

        const handleWheelScroll = (e) => {
            if (!isInitialized || entryBufferRef.current) return;

            const now = Date.now();
            const containerInView =
                containerRef.current &&
                containerRef.current.getBoundingClientRect().top <= 0 &&
                containerRef.current.getBoundingClientRect().bottom >=
                    window.innerHeight;

            if (containerInView || isComponentFullyVisible) {
                // Fallback to isComponentFullyVisible
                if (
                    now - lastScrollTimeRef.current < DEBOUNCE_TIME ||
                    isScrollingRef.current ||
                    isProcessingQueueRef.current
                ) {
                    e.preventDefault();
                    return;
                }

                const direction = e.deltaY > 0 ? 1 : -1;
                const nextSlide = activeSlide + direction;

                // Allow default scroll to exit section
                if (
                    (activeSlide === 0 && direction < 0) ||
                    (activeSlide === content.data.length - 1 && direction > 0)
                ) {
                    scrollAccumulatorRef.current = 0;
                    return;
                }

                e.preventDefault();

                // Normalize scroll delta to handle cross-browser differences
                const normalizedDelta = Math.min(Math.abs(e.deltaY), 100); // Cap delta to prevent large jumps
                scrollAccumulatorRef.current += normalizedDelta;

                if (scrollAccumulatorRef.current >= SCROLL_THRESHOLD) {
                    if (nextSlide >= 0 && nextSlide < content.data.length) {
                        lastScrollTimeRef.current = now;
                        scrollAccumulatorRef.current = 0; // Reset immediately
                        scrollQueueRef.current = [nextSlide];
                        processScrollQueue();
                    }
                }
            } else {
                scrollAccumulatorRef.current = 0;
            }
        };

        const handleKeyDown = (e) => {
            if (!isInitialized || !isComponentFullyVisible) return;

            if (
                containerRef.current &&
                containerRef.current.getBoundingClientRect().top <= 0 &&
                containerRef.current.getBoundingClientRect().bottom >=
                    window.innerHeight
            ) {
                let nextSlide = activeSlide;

                if (
                    e.key === "ArrowDown" &&
                    activeSlide < content.data.length - 1
                ) {
                    e.preventDefault();
                    nextSlide = activeSlide + 1;
                } else if (e.key === "ArrowUp" && activeSlide > 0) {
                    e.preventDefault();
                    nextSlide = activeSlide - 1;
                } else {
                    return;
                }

                if (
                    !isScrollingRef.current &&
                    !isProcessingQueueRef.current &&
                    Date.now() - lastScrollTimeRef.current > DEBOUNCE_TIME
                ) {
                    lastScrollTimeRef.current = Date.now();
                    scrollQueueRef.current = [nextSlide];
                    processScrollQueue();
                }
            }
        };

        window.addEventListener("wheel", handleWheelScroll, { passive: false });
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => {
            triggerRefs.current.forEach((ref) => {
                if (ref.current) {
                    observer.unobserve(ref.current);
                }
            });
            window.removeEventListener("wheel", handleWheelScroll);
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [activeSlide, content.data.length, isInitialized]);

    useEffect(() => {
        const fullScreenObserver = new IntersectionObserver(
            ([entry]) => {
                const fullyVisible = entry.intersectionRatio >= 0.8; // Lowered threshold
                setIsComponentFullyVisible(fullyVisible);
            },
            {
                threshold: [0.8, 1.0], // Adjusted for quicker response
            }
        );

        if (containerRef.current) {
            fullScreenObserver.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                fullScreenObserver.unobserve(containerRef.current);
            }
        };
    }, []);

    const handleDotClick = (index) => {
        if (isScrollingRef.current || isProcessingQueueRef.current) return;

        const now = Date.now();
        if (now - lastScrollTimeRef.current < DEBOUNCE_TIME) {
            return;
        }

        lastScrollTimeRef.current = now;
        scrollQueueRef.current = [index];
        processScrollQueue();
    };

    return (
        <>
            <div className="education-sector-container" ref={containerRef}>
                <h2 className="text-2xl md:text-3xl xl:text-4xl font-bold pb-7 text-center font-manrope">
                    {content?.title}
                </h2>

                <div className="sticky-section" ref={sectionRef}>
                    <div className="sticky-content">
                        <div className="slide-container">
                            {content.data.map((data, index) => (
                                <div
                                    key={data?.id || index}
                                    className={`slide ${
                                        isInitialized && index === activeSlide
                                            ? `active initialized ${
                                                  slideDirection === "forward"
                                                      ? "forward-enter"
                                                      : "backward-enter"
                                              }`
                                            : index === activeSlide
                                            ? "active"
                                            : ""
                                    } ${data.bgColor}`}
                                >
                                    <div className="w-full h-full py-20 md:px-5 px-2 container m-auto flex flex-col xl:flex-row justify-center items-center gap-5 xl:gap-14 lg:gap-10 md:gap-6 sm:gap-4 md:py-[450px] lg:py-20">
                                        <div className="w-full xl:w-[30%]">
                                            <div className="w-full flex flex-col justify-center items-start gap-3 mt-6">
                                                <Image
                                                    src={data.logo}
                                                    alt={data.logoName}
                                                    className={`${
                                                        data.logoName ==
                                                        "Haut.AI"
                                                            ? "w-[100px]"
                                                            : data.logoName ==
                                                              "Pixel Arcadia"
                                                            ? "w-[300px]"
                                                            : "w-[194px]"
                                                    }`}
                                                />
                                                <h3 className="font-manrope font-extrabold xl:text-4xl lg:text-3xl md:text-2xl text-xl xl:leading-10 leading-4">
                                                    {data.logoName}
                                                </h3>
                                                <span className="text-[#410EAD] font-inter font-medium sm:text-lg text-base sm:leading-5 leading-4">
                                                    {data.category}
                                                </span>
                                                <p className="font-inter font-medium sm:text-sm text-xs sm:leading-6 leading-4 tracking-wide">
                                                    {data.description}
                                                </p>
                                                <CommonButton
                                                    href={data.btnLink}
                                                    name={data.btnName}
                                                    arrow={true}
                                                    className="common-blue-button-style"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full xl:w-[70%] relative mt-8 md:mt-0">
                                            <Image
                                                src={data.sideDeskImg}
                                                alt={data.logoName}
                                                width={800}
                                                height={800}
                                                className="xl:w-full xl:h-full md:w-[500px] md:h-[400px] object-fill mx-auto"
                                            />
                                            <div className="absolute xl:-right-3 xl:-bottom-4 md:right-0 md:bottom-14 -right-3 -bottom-4 xl:w-[250px] 2xl:h-[500px] xl:h-[400px] md:w-40 md:h-56 w-24 h-40 [@media(min-width:1024px)_and_(max-width:1280px)]:right-36">
                                                <Image
                                                    src={data.sideMobileImg}
                                                    alt={data.logoName}
                                                    width={500}
                                                    height={500}
                                                    className="sm:w-full sm:h-full w-full h-full object-contain"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            className={`progress-indicator sm:right-[30px] right-3 ${
                                isInitialized ? "opacity-100" : "opacity-0"
                            } transition-opacity duration-500`}
                        >
                            {content.data.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-4 h-4 rounded-full cursor-pointer transition-all duration-300 flex items-center justify-center ${
                                        index === activeSlide && isInitialized
                                            ? "bg-indigo-700 scale-125"
                                            : "bg-gray-200 hover:bg-gray-300 hover:scale-110"
                                    }`}
                                    onClick={() => handleDotClick(index)}
                                >
                                    {index === activeSlide && isInitialized && (
                                        <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {content.data.map((_, index) => (
                    <div
                        key={index}
                        ref={(el) =>
                            (triggerRefs.current[index] = { current: el })
                        }
                        style={{ height: "100vh" }}
                        className="opacity-0 pointer-events-none"
                    />
                ))}
            </div>
        </>
    );
};

export default EducationSector;
